/** Throttle and brake along the lap, the chosen lap over the reference.
 *
 * The delta chart says where the time went; this says what the feet were
 * doing there. Two strips on the same axis as every other chart: throttle in
 * the good colour, brake in the warning colour, the chosen lap solid and the
 * reference dashed and quieter underneath. A lockup the logger detected is a
 * band on the brake strip and wheelspin a band on the throttle strip, so the
 * event sits on the pedal that caused it.
 *
 * Pedals rather than every channel, deliberately. The logger's view stacks
 * whatever the driver picks; this screen is a portal, read on a phone as
 * often as not, and the two inputs that decide a corner are the two worth the
 * room.
 */

import { Card, Kicker, Spinner } from '@/components/ui';
import { DASH } from '@/lib/format';
import { CornerAxis, CornerLines } from '@/components/sessions/CornerAxis';
import {
  PEDAL_BOX,
  channelPolyline,
  cornerTicks,
  distanceX,
  eventBands,
} from '@/components/sessions/compare';
import type { ComparedLap, CornerMark, EventBand } from '@/components/sessions/compare';
import type { Reference } from '@/components/sessions/references';

export interface InputsCardProps {
  lapNumber: number | null;
  reference: Reference | null;
  chosen: ComparedLap | null;
  against: ComparedLap | null;
  isReference: boolean;
  /** The axis both laps share, metres. */
  maxDistance: number;
  corners: readonly CornerMark[];
  loading: boolean;
}

const PEDALS = [
  { channel: 'throttle', label: 'throttle', colour: 'var(--color-ok)', event: 'wheelspin' },
  { channel: 'brake', label: 'brake', colour: 'var(--color-warn)', event: 'lockup' },
] as const;

export function InputsCard({
  lapNumber,
  reference,
  chosen,
  against,
  isReference,
  maxDistance,
  corners,
  loading,
}: InputsCardProps) {
  const ticks = cornerTicks(corners, maxDistance, PEDAL_BOX);
  const dist = chosen?.series['dist'];
  const ready = chosen && dist && dist.length > 1 && maxDistance > 0;
  const showReference = Boolean(against && reference && !isReference);

  return (
    <Card elevation="none" label="Pedals" style={{ padding: 12, gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <Kicker>
          pedals · lap {lapNumber ?? DASH}
          {showReference && reference ? ` vs ${reference.label}` : ''}
        </Kicker>
        {showReference ? (
          <span style={{ fontSize: 11, color: 'var(--color-neutral-500)' }}>
            solid this lap · dashed the reference
          </span>
        ) : null}
      </div>

      {!ready ? (
        <div
          style={{
            display: 'grid',
            placeItems: 'center',
            minHeight: PEDAL_BOX.height * 2,
            fontSize: 12.5,
            color: 'var(--color-neutral-600)',
          }}
        >
          {loading ? <Spinner label="loading the lap" /> : 'This lap has no pedal channels stored.'}
        </div>
      ) : (
        <>
          {PEDALS.map((pedal) => {
            const own = channelPolyline(dist, chosen.series[pedal.channel], maxDistance, 100);
            const theirs =
              showReference && against
                ? channelPolyline(against.series['dist'] ?? [], against.series[pedal.channel], maxDistance, 100)
                : '';
            const bands = eventBands(chosen.events, pedal.event);
            return (
              <div key={pedal.channel}>
                <svg
                  viewBox={`0 0 ${PEDAL_BOX.width} ${PEDAL_BOX.height}`}
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={`${pedal.label} along the lap`}
                  style={{ width: '100%', height: PEDAL_BOX.height, display: 'block' }}
                >
                  <CornerLines ticks={ticks} height={PEDAL_BOX.height} />
                  {bands.map((band, index) => (
                    <Band key={index} band={band} maxDistance={maxDistance} colour={pedal.colour} />
                  ))}
                  <line
                    x1={0}
                    y1={PEDAL_BOX.height - 1}
                    x2={PEDAL_BOX.width}
                    y2={PEDAL_BOX.height - 1}
                    stroke="var(--color-divider)"
                    vectorEffect="non-scaling-stroke"
                  />
                  {theirs ? (
                    <polyline
                      points={theirs}
                      fill="none"
                      stroke={pedal.colour}
                      strokeOpacity={0.55}
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      vectorEffect="non-scaling-stroke"
                    />
                  ) : null}
                  <polyline
                    points={own}
                    fill="none"
                    stroke={pedal.colour}
                    strokeWidth={1.3}
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
                <div style={{ fontSize: 10, color: 'var(--color-neutral-600)', marginTop: 2 }}>
                  {pedal.label}
                  {bands.length > 0
                    ? ` · ${bands.length} ${pedal.event}${bands.length === 1 ? '' : 's'}`
                    : ''}
                </div>
              </div>
            );
          })}
          <CornerAxis ticks={ticks} width={PEDAL_BOX.width} />
        </>
      )}
    </Card>
  );
}

function Band({ band, maxDistance, colour }: { band: EventBand; maxDistance: number; colour: string }) {
  const x = distanceX(band.from, maxDistance, PEDAL_BOX);
  const end = distanceX(band.to, maxDistance, PEDAL_BOX);
  return (
    <rect x={x} y={0} width={Math.max(end - x, 1)} height={PEDAL_BOX.height} fill={colour} fillOpacity={0.18}>
      <title>
        {band.type} · {band.wheels || 'wheels unknown'}
        {band.severity === null ? '' : ` · severity ${band.severity.toFixed(2)}`}
      </title>
    </rect>
  );
}

export default InputsCard;
